import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { questionnaireService } from '../services/questionnaireService';
import {
  indicatorService,
  IndicatorResponse,
} from '../services/indicatorService';
import {
  statisticsService,
  IndicatorStatistics,
  ClassStatistics,
  GradeStatistics,
  ConclusionUser,
  QuestionnaireAnswer,
  ConclusionDetail,
} from '../services/statisticsService';
import { Questionnaire } from '../types';
import { ArrowLeftIcon, AlertTriangleIcon, ShieldCheckIcon } from '../components/Icons';
import { useToast } from '../context/ToastContext';
import ReportPreviewView from '../components/ReportPreviewView';
import { reportService, ScopeSchool, ScopeClass } from '../services/reportService';
import { answerService, QuestionnaireAnswer as AnswerListItem, AnswerStatus, DurationBucket, DURATION_BUCKETS } from '../services/answerService';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

const getLevelColor = (level: string | null | undefined) => level || '#6b7280';

/**
 * 把 conclusionRule 的 conditionType + conditionValue（JSON 字符串）
 * 翻译成自然语言（"分数 ≥ 10" / "分数 = 5" / "得分在 0 到 20 之间（含）"）。
 * 用于「指标规则」卡片展示。
 */
const formatRuleCondition = (conditionType: string, conditionValue: string): string => {
  let cfg: any = {};
  try { cfg = JSON.parse(conditionValue || '{}'); } catch { /* 配置坏掉则降级显示原文 */ }

  switch (conditionType) {
    case 'SCORE_RANGE': {
      const { min, max, minInclusive, maxInclusive } = cfg;
      const hasMin = min !== null && min !== undefined;
      const hasMax = max !== null && max !== undefined;
      if (hasMin && hasMax) {
        return `分数 ${minInclusive ? '≥' : '>'} ${min} 且 ${maxInclusive ? '≤' : '<'} ${max}`;
      }
      if (hasMin) return `分数 ${minInclusive ? '≥' : '>'} ${min}`;
      if (hasMax) return `分数 ${maxInclusive ? '≤' : '<'} ${max}`;
      return '分数任意';
    }
    case 'SCORE_EQUALS':
      return `分数 = ${cfg.value ?? '?'}`;
    case 'TEXT_EQUALS':
      return `文本 = "${cfg.value ?? ''}"`;
    case 'EXPRESSION':
      return `满足表达式：${cfg.expression ?? conditionValue}`;
    default:
      return conditionValue;
  }
};

const StatisticsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast } = useToast();

  const locationState = location.state as { schoolId?: number; from?: string } | null;
  const initialSchoolId = locationState?.schoolId;
  const fromSchoolData = locationState?.from === 'school-data';

  const handleBack = () => {
    if (fromSchoolData && initialSchoolId) {
      navigate(`/schools/${initialSchoolId}/data`);
    } else {
      navigate(`/questionnaires/${id}`);
    }
  };
  const backLabel = fromSchoolData ? '返回数据管理' : '返回问卷详情';

  const [questionnaire, setQuestionnaire] = useState<Questionnaire | null>(null);
  const [indicators, setIndicators] = useState<IndicatorResponse[]>([]);
  const [selectedIndicator, setSelectedIndicator] = useState<number | null>(null);
  const [statistics, setStatistics] = useState<IndicatorStatistics | null>(null);
  const [classStats, setClassStats] = useState<ClassStatistics[]>([]);
  const [gradeStats, setGradeStats] = useState<GradeStatistics[]>([]);
  const [loading, setLoading] = useState(true);

  // School filter (snapshot-based, shared across all tabs)
  const [schools, setSchools] = useState<ScopeSchool[]>([]);
  const [classes, setClasses] = useState<ScopeClass[]>([]);
  const [loadingClasses, setLoadingClasses] = useState(false);
  const [selectedSchool, setSelectedSchool] = useState<number | undefined>(initialSchoolId);
  const [selectedYear, setSelectedYear] = useState<string | undefined>(undefined);
  const [selectedClassId, setSelectedClassId] = useState<string | undefined>(undefined);

  const selectedSchoolName = useMemo(
    () => schools.find(s => s.schoolId === selectedSchool)?.schoolName,
    [schools, selectedSchool]
  );
  const selectedClassName = useMemo(
    () => classes.find(c => c.classId === selectedClassId)?.className,
    [classes, selectedClassId]
  );

  // classId 前 4 位 = 学年，从已有班级里抽出独立学年列表
  const availableYears = useMemo(() => {
    const set = new Set<string>();
    classes.forEach(c => {
      if (c.classId && c.classId.length >= 4) set.add(c.classId.substring(0, 4));
    });
    return Array.from(set).sort().reverse();
  }, [classes]);

  // 班级下拉按学年过滤
  const visibleClasses = useMemo(() => {
    if (!selectedYear) return classes;
    return classes.filter(c => c.classId.startsWith(selectedYear + '-'));
  }, [classes, selectedYear]);

  // Crisis alert
  const [crisisAlertUsers, setCrisisAlertUsers] = useState<ConclusionUser[]>([]);
  const [crisisAlertSummary, setCrisisAlertSummary] = useState<{
    alertUsers: number;
    alertOccurrences: number;
  } | null>(null);
  const [crisisAlertOpen, setCrisisAlertOpen] = useState(false);
  const [loadingCrisisAlert, setLoadingCrisisAlert] = useState(false);

  // Active tab：indicators / report / answers
  type TabKey = 'indicators' | 'report' | 'answers';
  const parseTab = (s: string | null): TabKey => {
    if (s === 'report' || s === 'answers') return s;
    return 'indicators';
  };
  const [activeTab, setActiveTab] = useState<TabKey>(
    parseTab(new URLSearchParams(location.search).get('tab'))
  );

  // 没配置指标时，默认切到"答题结果" tab — 否则用户进来一片空白以为坏了
  useEffect(() => {
    if (loading) return;
    const urlTab = new URLSearchParams(location.search).get('tab');
    if (!urlTab && indicators.length === 0 && activeTab === 'indicators') {
      setActiveTab('answers');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, indicators.length]);
  const handleTabChange = (next: TabKey) => {
    setActiveTab(next);
    const params = new URLSearchParams(location.search);
    if (next === 'indicators') params.delete('tab');
    else params.set('tab', next);
    navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
  };

  useEffect(() => {
    if (id) {
      fetchData();
    }
  }, [id]);

  useEffect(() => {
    if (selectedIndicator && id) {
      fetchStatistics();
    }
  }, [selectedIndicator, selectedSchool]);

  // 学校变化 → 拉对应班级；学年/班级清空
  useEffect(() => {
    if (!id) return;
    setSelectedYear(undefined);
    setSelectedClassId(undefined);
    if (!selectedSchool) {
      setClasses([]);
      return;
    }
    let cancelled = false;
    setLoadingClasses(true);
    (async () => {
      try {
        const cls = await reportService.getScopeClasses(Number(id), selectedSchool);
        if (!cancelled) setClasses(cls);
      } catch {
        if (!cancelled) setClasses([]);
      } finally {
        if (!cancelled) setLoadingClasses(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedSchool, id]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [questionnaireData, indicatorsData, scopeSchools] = await Promise.all([
        questionnaireService.getQuestionnaire(Number(id)),
        indicatorService.getIndicators(Number(id)),
        reportService.getScopeSchools(Number(id)),
      ]);
      setQuestionnaire(questionnaireData);
      setIndicators(indicatorsData);
      setSchools(scopeSchools);

      if (indicatorsData.length > 0) {
        setSelectedIndicator(indicatorsData[0].id);
      }

      // Fetch crisis alert users
      fetchCrisisAlertUsers(Number(id));
    } catch (error) {
      console.error('Failed to fetch data:', error);
      showToast('加载失败', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchCrisisAlertUsers = async (questionnaireId: number) => {
    try {
      setLoadingCrisisAlert(true);
      // 主数字（人数 + 次数）走 summary；列表懒加载到展开时再说不太合理，
      // 现状是一次性都拉，这里继续保持，但 summary 独立失败也不影响列表渲染。
      const [users, summary] = await Promise.all([
        statisticsService.getCrisisAlertUsers(questionnaireId),
        statisticsService.getCrisisAlertSummary(questionnaireId).catch((e) => {
          console.error('Failed to fetch crisis alert summary:', e);
          return null;
        }),
      ]);
      setCrisisAlertUsers(users);
      setCrisisAlertSummary(summary);
    } catch (error) {
      console.error('Failed to fetch crisis alert users:', error);
    } finally {
      setLoadingCrisisAlert(false);
    }
  };

  const fetchStatistics = async () => {
    if (!selectedIndicator || !id) return;
    const questionnaireId = Number(id);

    try {
      // Always fetch overall stats
      const overallStats = await statisticsService.getIndicatorStatistics(questionnaireId, selectedIndicator, selectedSchool);
      setStatistics(overallStats);

      // Only fetch grade/class breakdowns when a school is selected
      if (selectedSchool) {
        const [classSt, gradeSt] = await Promise.all([
          statisticsService.getStatisticsByClass(questionnaireId, selectedIndicator, selectedSchool),
          statisticsService.getStatisticsByGrade(questionnaireId, selectedIndicator, selectedSchool),
        ]);
        setClassStats(classSt);
        setGradeStats(gradeSt);
      } else {
        setClassStats([]);
        setGradeStats([]);
      }
    } catch (error) {
      console.error('Failed to fetch statistics:', error);
      showToast('加载统计数据失败', 'error');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-on-surface-variant">加载中...</div>
      </div>
    );
  }

  // 注意：以前在这里 short-circuit 整页（indicators.length===0 直接返回 CTA），
  // 但"答题结果"和"大报告" tab 跟指标无关，没指标也应该能看 — bug 2026-05-12。
  // 现在改成保留整页，把"未配置指标"做成"指标统计" tab 内部的空状态。

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={handleBack}
          className="flex items-center text-on-surface-variant hover:text-on-surface mb-4"
        >
          <ArrowLeftIcon className="w-5 h-5 mr-2" />
          {backLabel}
        </button>
        <div>
          <h1 className="text-3xl font-bold text-on-surface font-headline">统计分析</h1>
          <p className="mt-2 text-on-surface-variant font-body">{questionnaire?.title}</p>
        </div>
      </div>

      {/* 统一 Filter 行：学校 ↔ 班级联动；指标统计 tab 额外有指标选择器 */}
      <div className="mb-5 bg-surface-container-lowest rounded-3xl shadow-soft px-5 py-4">
        <div className="flex flex-wrap items-end gap-4">
          {/* 学校 */}
          <div className="min-w-[14rem]">
            <label className="block text-xs font-medium text-on-surface-variant font-label mb-1.5">学校</label>
            <select
              value={selectedSchool ?? ''}
              onChange={(e) => setSelectedSchool(e.target.value ? Number(e.target.value) : undefined)}
              className="w-full h-10 px-3 bg-white border border-outline-variant rounded-xl text-sm text-on-surface focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary"
            >
              <option value="">全部学校</option>
              {schools.map((s) => (
                <option key={s.schoolId} value={s.schoolId}>{s.schoolName}</option>
              ))}
            </select>
          </div>

          {/* 学年（依赖学校；从有数据的班级 classId 前缀推导） */}
          <div className="min-w-[10rem]">
            <label className="block text-xs font-medium text-on-surface-variant font-label mb-1.5">学年</label>
            <select
              value={selectedYear ?? ''}
              onChange={(e) => {
                setSelectedYear(e.target.value || undefined);
                setSelectedClassId(undefined);
              }}
              disabled={!selectedSchool || loadingClasses || availableYears.length === 0}
              className="w-full h-10 px-3 bg-white border border-outline-variant rounded-xl text-sm text-on-surface focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary disabled:bg-surface-container-low disabled:text-on-surface-variant disabled:cursor-not-allowed"
            >
              <option value="">{!selectedSchool ? '请先选学校' : loadingClasses ? '加载中…' : '全部学年'}</option>
              {availableYears.map((y) => (
                <option key={y} value={y}>{y} 学年</option>
              ))}
            </select>
          </div>

          {/* 班级（依赖学校 + 学年） */}
          <div className="min-w-[14rem]">
            <label className="block text-xs font-medium text-on-surface-variant font-label mb-1.5">班级</label>
            <select
              value={selectedClassId ?? ''}
              onChange={(e) => setSelectedClassId(e.target.value || undefined)}
              disabled={!selectedSchool || loadingClasses}
              className="w-full h-10 px-3 bg-white border border-outline-variant rounded-xl text-sm text-on-surface focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary disabled:bg-surface-container-low disabled:text-on-surface-variant disabled:cursor-not-allowed"
            >
              <option value="">{!selectedSchool ? '请先选学校' : loadingClasses ? '加载中…' : '全部班级'}</option>
              {visibleClasses.map((c) => (
                <option key={c.classId} value={c.classId}>
                  {c.grade ? `${c.grade} · ${c.className}` : c.className}
                </option>
              ))}
            </select>
          </div>

          {/* 指标（仅在 indicators tab 且有指标可选时显示） */}
          {activeTab === 'indicators' && indicators.length > 0 && (
            <div className="min-w-[14rem]">
              <label className="block text-xs font-medium text-on-surface-variant font-label mb-1.5">指标</label>
              <select
                value={selectedIndicator || ''}
                onChange={(e) => setSelectedIndicator(Number(e.target.value))}
                className="w-full h-10 px-3 bg-white border border-outline-variant rounded-xl text-sm text-on-surface focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary"
              >
                {indicators.map((indicator) => (
                  <option key={indicator.id} value={indicator.id}>{indicator.name}</option>
                ))}
              </select>
            </div>
          )}

          {(selectedSchool || selectedYear || selectedClassId) && (
            <button
              type="button"
              onClick={() => {
                setSelectedSchool(undefined);
                setSelectedYear(undefined);
                setSelectedClassId(undefined);
              }}
              className="h-10 px-3 inline-flex items-center gap-1 text-sm text-on-surface-variant hover:text-primary"
            >
              <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>close</span>
              清除筛选
            </button>
          )}
        </div>
      </div>

      {/* Tab nav */}
      <div className="mb-6 border-b border-outline-variant flex gap-2">
        {[
          { key: 'indicators' as const, label: '指标统计', icon: 'analytics' },
          { key: 'report' as const,    label: '大报告',   icon: 'description' },
          { key: 'answers' as const,   label: '答题结果', icon: 'fact_check' },
        ].map((t) => {
          const active = activeTab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => handleTabChange(t.key)}
              className={`px-5 h-11 inline-flex items-center gap-2 text-sm font-medium border-b-2 transition-colors ${
                active
                  ? 'border-primary text-primary'
                  : 'border-transparent text-on-surface-variant hover:text-on-surface'
              }`}
            >
              <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>{t.icon}</span>
              {t.label}
            </button>
          );
        })}
      </div>

      {activeTab === 'report' && id && (
        <ReportPreviewView
          title={questionnaire?.title}
          loadPreview={(scope) => reportService.getPreview(Number(id), scope)}
          loadSchools={() => reportService.getScopeSchools(Number(id))}
          loadClasses={(sId) => reportService.getScopeClasses(Number(id), sId)}
          controlledScope={{ schoolId: selectedSchool, classId: selectedClassId }}
        />
      )}

      {activeTab === 'answers' && id && (
        <AnswerListView
          questionnaireId={Number(id)}
          schoolName={selectedSchoolName}
          className={selectedClassName}
          academicYear={selectedYear}
        />
      )}

      {activeTab === 'indicators' && indicators.length === 0 && (
        <div className="bg-surface-container-lowest rounded-3xl shadow-soft p-8 text-center">
          <p className="text-on-surface-variant font-body mb-4">该问卷尚未配置指标</p>
          <button
            onClick={() => navigate(`/questionnaires/${id}/indicators`)}
            className="gradient-primary text-on-primary px-4 py-2 rounded-xl shadow-teal hover:shadow-soft-md"
          >
            前往配置指标
          </button>
          <p className="text-xs text-on-surface-variant font-body mt-4">
            没有指标也可以在"答题结果"和"大报告" tab 查看原始数据
          </p>
        </div>
      )}

      {activeTab === 'indicators' && indicators.length > 0 && (
      <>
      {/* Crisis Alert Card */}
      <CrisisAlertCard
        users={crisisAlertUsers}
        summary={crisisAlertSummary}
        loading={loadingCrisisAlert}
        open={crisisAlertOpen}
        onToggle={() => setCrisisAlertOpen(!crisisAlertOpen)}
      />

      {/* Overall Statistics (always shown) */}
      {statistics && (
        <OverallStatistics
          statistics={statistics}
          indicator={indicators.find((i) => i.id === selectedIndicator) || null}
        />
      )}

      {/* Grade & Class Breakdown — only when school is selected */}
      {selectedSchool ? (
        (() => {
          // 班级 → 唯一班级；学年 → 按 classId 前缀筛；都没选 → 全部
          let filteredClassStats = classStats;
          if (selectedClassId) {
            filteredClassStats = classStats.filter(c => c.classId === selectedClassId);
          } else if (selectedYear) {
            filteredClassStats = classStats.filter(c => c.classId.startsWith(selectedYear + '-'));
          }
          const showGrade = !selectedClassId && gradeStats.length > 0;
          if (filteredClassStats.length === 0 && !showGrade) return null;
          return (
            <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
              {showGrade && (
                <GradeStatisticsView stats={gradeStats} />
              )}
              {filteredClassStats.length > 0 && (
                <ClassStatisticsView stats={filteredClassStats} />
              )}
            </div>
          );
        })()
      ) : schools.length > 0 ? (
        <div className="mt-6 bg-surface-container-lowest rounded-3xl shadow-soft p-6 text-center text-on-surface-variant font-body">
          请在上方选择学校以查看年级和班级对比数据
        </div>
      ) : null}
      </>
      )}
    </div>
  );
};

// Crisis Alert Card Component
interface CrisisAlertCardProps {
  users: ConclusionUser[];
  /** summary 优先用于卡片主数字（人数 + 次数）；为 null 时回退到 users.length */
  summary: { alertUsers: number; alertOccurrences: number } | null;
  loading: boolean;
  open: boolean;
  onToggle: () => void;
}

const CrisisAlertCard: React.FC<CrisisAlertCardProps> = ({ users, summary, loading, open, onToggle }) => {
  const [expandedAnswerId, setExpandedAnswerId] = useState<number | null>(null);
  const [answerDetails, setAnswerDetails] = useState<Record<number, QuestionnaireAnswer>>({});
  const [loadingAnswer, setLoadingAnswer] = useState<number | null>(null);

  // 按 answerId 聚合 — 一个学生一行，命中的多个指标合并展示
  type GroupedAlert = {
    answerId: number;
    userName: string;
    studentNumber: string;
    schoolName: string;
    className: string;
    hits: Array<{
      indicatorName: string;
      conclusionTitle?: string;
      conclusionLevel?: string;
      score: number;
    }>;
  };
  const groupedUsers: GroupedAlert[] = React.useMemo(() => {
    const map = new Map<number, GroupedAlert>();
    for (const u of users) {
      const existing = map.get(u.answerId);
      const hit = {
        indicatorName: u.indicatorName || '-',
        conclusionTitle: u.conclusionTitle,
        conclusionLevel: u.conclusionLevel,
        score: u.score,
      };
      if (existing) {
        existing.hits.push(hit);
      } else {
        map.set(u.answerId, {
          answerId: u.answerId,
          userName: u.userName,
          studentNumber: u.studentNumber,
          schoolName: u.schoolName,
          className: u.className,
          hits: [hit],
        });
      }
    }
    return Array.from(map.values());
  }, [users]);

  // 危机预警色板：暖色警告系（红橙黄棕），全部避开绿色 —— 所有命中都是预警，
  // 用 level 那一套颜色会全部映射到同一个 hex（重叠），所以这里按 conclusionTitle
  // 出现顺序在本地分配独立颜色。**仅本卡片用，不动 getLevelColor 全局函数**
  // （它还被下方的饼图/柱图/详情 chip 复用）。
  // 避开绿色/青色系，色相拉开（红 → 橙 → 黄 → 玫红 → 紫红 → 棕），
  // 任意两色一眼能区分。
  const ALERT_PALETTE = [
    '#dc2626', // 鲜红
    '#f97316', // 亮橙
    '#facc15', // 鲜黄
    '#ec4899', // 玫红/品红
    '#a855f7', // 紫
    '#78350f', // 深棕
    '#0ea5e9', // 天蓝（备用，色相离前面最远）
    '#6b7280', // 灰（兜底）
  ];
  const titleToColor: Map<string, string> = React.useMemo(() => {
    const m = new Map<string, string>();
    let i = 0;
    for (const u of users) {
      if (u.conclusionTitle && !m.has(u.conclusionTitle)) {
        m.set(u.conclusionTitle, ALERT_PALETTE[i % ALERT_PALETTE.length]);
        i++;
      }
    }
    return m;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users]);
  const alertColorFor = (title?: string) => (title ? titleToColor.get(title) || '#6b7280' : '#6b7280');

  // 图例：出现过的 conclusionTitle，配色用本地 palette（去重）
  const legend: Array<{ title: string; color: string }> = React.useMemo(() => {
    return Array.from(titleToColor.entries()).map(([title, color]) => ({ title, color }));
  }, [titleToColor]);

  const handleUserClick = async (answerId: number) => {
    if (expandedAnswerId === answerId) {
      setExpandedAnswerId(null);
      return;
    }
    setExpandedAnswerId(answerId);
    if (!answerDetails[answerId]) {
      setLoadingAnswer(answerId);
      try {
        const detail = await statisticsService.getAnswerDetail(answerId);
        setAnswerDetails((prev) => ({ ...prev, [answerId]: detail }));
      } catch (error) {
        console.error('Failed to load answer detail:', error);
      } finally {
        setLoadingAnswer(null);
      }
    }
  };

  // 人数 (distinct user) — 卡片主数字 / 徽章 / 副标题主体
  // 次数 (occurrences) — 同一学生在多个 crisis 指标都中算多次，副标题加注
  // summary 失败时回退用 users.length（实际是「次数」语义，保旧行为兜底）
  const alertUsers = summary ? summary.alertUsers : users.length;
  const alertOccurrences = summary ? summary.alertOccurrences : users.length;
  const hasAlert = alertUsers > 0;

  return (
    <div className="mb-6">
      <div
        className={`rounded-3xl shadow-soft cursor-pointer transition-colors ${
          hasAlert ? 'bg-red-50 border-2 border-red-300 hover:bg-red-100' : 'bg-surface-container-lowest hover:bg-surface-container-low'
        }`}
        onClick={onToggle}
      >
        <div className="px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {hasAlert ? (
              <AlertTriangleIcon size={24} className="text-red-600" />
            ) : (
              <ShieldCheckIcon size={24} className="text-green-600" />
            )}
            <div>
              <h3 className={`text-lg font-semibold font-headline ${hasAlert ? 'text-red-800' : 'text-on-surface'}`}>
                危机预警
              </h3>
              <p className={`text-sm font-body ${hasAlert ? 'text-red-600' : 'text-on-surface-variant'}`}>
                {loading
                  ? '加载中...'
                  : hasAlert
                  ? `${alertUsers} 名学生触发预警，共发生 ${alertOccurrences} 次（同一学生在多个指标上均触发会重复计入次数）`
                  : '暂无危机预警'}
              </p>
            </div>
          </div>
          {hasAlert && (
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1.5 bg-red-600 text-white px-3 py-1 rounded-full">
                <span className="text-sm font-bold">{alertUsers}</span>
                <span className="text-xs opacity-90">人</span>
              </div>
              <div className="flex items-center gap-1.5 bg-red-100 text-red-700 px-3 py-1 rounded-full border border-red-300">
                <span className="text-sm font-bold">{alertOccurrences}</span>
                <span className="text-xs opacity-90">次</span>
              </div>
              <span className="text-on-surface-variant text-sm">{open ? '▲' : '▼'}</span>
            </div>
          )}
        </div>
      </div>

      {/* Expanded crisis alert user list */}
      {open && hasAlert && (
        <div className="mt-2 bg-surface-container-lowest rounded-3xl shadow-soft border border-red-200 overflow-hidden">
          {/* 图例：表格下面每行的"结论"列只画色块以节省空间，全名靠这里认色 */}
          {legend.length > 0 && (
            <div className="px-6 py-3 bg-red-50/50 border-b border-red-100">
              <div className="flex items-center gap-4 flex-wrap">
                <span className="text-xs font-medium text-red-700 font-label">结论图例：</span>
                {legend.map((l) => (
                  <div key={l.title} className="flex items-center gap-1.5">
                    <span
                      className="inline-block w-3 h-3 rounded-full"
                      style={{ backgroundColor: l.color }}
                    />
                    <span className="text-xs text-on-surface font-body">{l.title}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          <table className="min-w-full">
            <thead className="bg-red-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-red-700 uppercase font-label">姓名</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-red-700 uppercase font-label">学号</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-red-700 uppercase font-label">学校</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-red-700 uppercase font-label">班级</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-red-700 uppercase font-label">命中结论</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-red-700 uppercase font-label">详情</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-red-700 uppercase font-label w-10"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {groupedUsers.map((user) => (
                <React.Fragment key={user.answerId}>
                  <tr
                    className="hover:bg-red-50 cursor-pointer transition-colors"
                    onClick={() => handleUserClick(user.answerId)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-on-surface align-top">
                      {user.userName || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-on-surface-variant align-top">
                      {user.studentNumber || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-on-surface-variant align-top">
                      {user.schoolName || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-on-surface-variant align-top">
                      {user.className || '-'}
                    </td>
                    <td className="px-6 py-4 text-sm text-on-surface-variant align-top">
                      <div className="flex items-center flex-wrap gap-1.5">
                        {user.hits.map((h, idx) => (
                          <span
                            key={idx}
                            title={`${h.indicatorName}${h.conclusionTitle ? ' · ' + h.conclusionTitle : ''}${h.score != null ? '（' + h.score.toFixed(2) + '分）' : ''}`}
                            className="inline-block w-3.5 h-3.5 rounded-full shrink-0"
                            style={{ backgroundColor: alertColorFor(h.conclusionTitle) }}
                          />
                        ))}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm font-medium text-on-surface align-top whitespace-nowrap">
                      {user.hits.length === 1
                        ? (user.hits[0].score?.toFixed(2) ?? '-')
                        : `命中 ${user.hits.length} 个指标`}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-on-surface-variant align-top">
                      {expandedAnswerId === user.answerId ? '▲' : '▼'}
                    </td>
                  </tr>
                  {expandedAnswerId === user.answerId && (
                    <tr>
                      <td colSpan={7} className="px-6 py-0">
                        <div className="bg-surface-container-low rounded-2xl p-4 my-2 shadow-soft">
                          {loadingAnswer === user.answerId ? (
                            <div className="text-center text-on-surface-variant py-4 font-body">加载回答详情...</div>
                          ) : answerDetails[user.answerId] ? (
                            <div className="space-y-3">
                              <h4 className="text-sm font-semibold text-on-surface font-label mb-2">回答详情</h4>
                              {answerDetails[user.answerId].answerDetails
                                .filter((d) => d.questionType !== 'DESCRIPTION')
                                .map((d) => (
                                  <div key={d.questionId} className="text-sm">
                                    <AnswerDetailBlock detail={d} />
                                  </div>
                                ))}
                              {answerDetails[user.answerId].indicatorResults.length > 0 && (
                                <>
                                  <hr style={{ borderTop: '1px solid rgba(171, 179, 183, 0.2)' }} />
                                  <h4 className="text-sm font-semibold text-on-surface font-label mb-2">指标结果</h4>
                                  {answerDetails[user.answerId].indicatorResults.map((ir) => (
                                    <div key={ir.indicatorId} className="flex items-center gap-2 text-sm">
                                      <span className="text-on-surface font-medium font-body">{ir.indicatorName}:</span>
                                      <span className="text-on-surface font-semibold font-body">{ir.score?.toFixed(2) ?? '-'}</span>
                                      {ir.conclusionTitle && (
                                        <span
                                          className="px-2 py-0.5 text-xs font-medium rounded-full text-white"
                                          style={{ backgroundColor: getLevelColor(ir.level) }}
                                        >
                                          {ir.conclusionTitle}
                                        </span>
                                      )}
                                    </div>
                                  ))}
                                </>
                              )}
                            </div>
                          ) : (
                            <div className="text-center text-on-surface-variant py-4 font-body">加载失败</div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// Overall Statistics Component
interface OverallStatisticsProps {
  statistics: IndicatorStatistics;
  /** 当前选中的指标 — 用于「指标规则」卡片，切换指标时父组件传新对象 */
  indicator: IndicatorResponse | null;
}

const OverallStatistics: React.FC<OverallStatisticsProps> = ({ statistics, indicator }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [modalConclusion, setModalConclusion] = useState<ConclusionDetail | null>(null);
  const [conclusionUsers, setConclusionUsers] = useState<ConclusionUser[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [expandedAnswerId, setExpandedAnswerId] = useState<number | null>(null);
  const [answerDetails, setAnswerDetails] = useState<Record<number, QuestionnaireAnswer>>({});
  const [loadingAnswer, setLoadingAnswer] = useState<number | null>(null);

  const handleConclusionClick = async (detail: ConclusionDetail) => {
    setModalConclusion(detail);
    setModalOpen(true);
    setExpandedAnswerId(null);
    setLoadingUsers(true);
    try {
      const users = await statisticsService.getConclusionUsers(statistics.indicatorId, detail.conclusionTitle);
      setConclusionUsers(users);
    } catch (error) {
      console.error('Failed to load conclusion users:', error);
      setConclusionUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleUserClick = async (answerId: number) => {
    if (expandedAnswerId === answerId) {
      setExpandedAnswerId(null);
      return;
    }
    setExpandedAnswerId(answerId);
    if (!answerDetails[answerId]) {
      setLoadingAnswer(answerId);
      try {
        const detail = await statisticsService.getAnswerDetail(answerId);
        setAnswerDetails((prev) => ({ ...prev, [answerId]: detail }));
      } catch (error) {
        console.error('Failed to load answer detail:', error);
      } finally {
        setLoadingAnswer(null);
      }
    }
  };

  const closeModal = () => {
    setModalOpen(false);
    setModalConclusion(null);
    setConclusionUsers([]);
    setExpandedAnswerId(null);
  };

  const pieData = statistics.conclusions.map((c) => ({
    name: c.conclusionTitle,
    value: c.count,
    level: c.level,
    percentage: c.percentage,
  }));

  const barData = statistics.conclusions.map((c) => ({
    name: c.conclusionTitle,
    count: c.count,
    level: c.level,
  }));

  // 当前指标对象（用于"指标规则"卡片）— 由父组件按 selectedIndicator 传入
  const currentIndicator = indicator;

  return (
    <div className="space-y-6">
      {/* 当前指标的规则（自然语言） */}
      {currentIndicator && (
        <div className="bg-surface-container-lowest rounded-3xl shadow-soft p-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-bold font-headline text-on-surface">
              {currentIndicator.name} · 规则说明
            </h3>
            {currentIndicator.description && (
              <span className="text-xs text-on-surface-variant">{currentIndicator.description}</span>
            )}
          </div>
          {currentIndicator.conclusionRules && currentIndicator.conclusionRules.length > 0 ? (
            <ul className="space-y-2 text-sm">
              {[...currentIndicator.conclusionRules]
                .sort((a, b) => (a.orderNum || 0) - (b.orderNum || 0))
                .map((rule) => (
                  <li key={rule.id} className="flex items-start gap-2">
                    <span
                      className="mt-1 inline-block h-2.5 w-2.5 rounded-full shrink-0"
                      style={{ backgroundColor: rule.level || '#94a3b8' }}
                    />
                    <span>
                      <span className="text-on-surface-variant">当 </span>
                      <span className="font-medium">{formatRuleCondition(rule.conditionType, rule.conditionValue)}</span>
                      <span className="text-on-surface-variant"> 时判为 </span>
                      <span className="font-semibold" style={{ color: rule.level || '#1f2937' }}>
                        「{rule.conclusionTitle}」
                      </span>
                      {rule.crisisAlert && (
                        <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-danger-50 text-danger-700 border border-danger-200">
                          危机预警
                        </span>
                      )}
                    </span>
                  </li>
                ))}
            </ul>
          ) : (
            <div className="text-sm text-on-surface-variant">未配置结论规则</div>
          )}
          <div className="mt-3 text-xs text-on-surface-variant border-t border-outline-variant pt-2">
            <span>未匹配任何规则的得分 → 「正常范围」</span>
          </div>
        </div>
      )}

      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-surface-container-lowest rounded-3xl shadow-soft p-6">
          <div className="text-sm text-on-surface-variant font-label mb-1">总人数</div>
          <div className="text-3xl font-bold text-on-surface font-headline">{statistics.totalAnswers}</div>
        </div>
        <div className="bg-surface-container-lowest rounded-3xl shadow-soft p-6">
          <div className="text-sm text-on-surface-variant font-label mb-1">平均分</div>
          <div className="text-3xl font-bold text-blue-600 font-headline">
            {(statistics.avgScore ?? 0).toFixed(2)}
          </div>
        </div>
        <div className="bg-surface-container-lowest rounded-3xl shadow-soft p-6">
          <div className="text-sm text-on-surface-variant font-label mb-1">最低分</div>
          <div className="text-3xl font-bold text-orange-600 font-headline">
            {(statistics.minScore ?? 0).toFixed(2)}
          </div>
        </div>
        <div className="bg-surface-container-lowest rounded-3xl shadow-soft p-6">
          <div className="text-sm text-on-surface-variant font-label mb-1">最高分</div>
          <div className="text-3xl font-bold text-green-600 font-headline">
            {(statistics.maxScore ?? 0).toFixed(2)}
          </div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Donut Pie Chart */}
        <div className="bg-surface-container-lowest rounded-3xl shadow-soft p-6">
          <h3 className="text-lg font-semibold text-on-surface font-headline mb-4">结论分布</h3>
          {pieData.length > 0 ? (
            <ResponsiveContainer width="100%" height={320}>
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={70}
                  outerRadius={110}
                  paddingAngle={2}
                  dataKey="value"
                  label={({ name, percent }: any) => `${name} ${(percent * 100).toFixed(1)}%`}
                >
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={getLevelColor(entry.level)} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value: any, name: any) => [`${value}人`, name]}
                />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-64 text-on-surface-variant font-body">暂无数据</div>
          )}
        </div>

        {/* Bar Chart */}
        <div className="bg-surface-container-lowest rounded-3xl shadow-soft p-6">
          <h3 className="text-lg font-semibold text-on-surface font-headline mb-4">结论人数分布</h3>
          {barData.length > 0 ? (
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={barData} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis allowDecimals={false} />
                <Tooltip
                  formatter={(value: any) => [`${value}人`, '人数']}
                />
                <Bar dataKey="count" name="人数" radius={[4, 4, 0, 0]}>
                  {barData.map((entry, index) => (
                    <Cell key={`bar-${index}`} fill={getLevelColor(entry.level)} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-64 text-on-surface-variant font-body">暂无数据</div>
          )}
        </div>
      </div>

      {/* Conclusion Details Table */}
      <div className="bg-surface-container-lowest rounded-3xl shadow-soft p-6">
        <h3 className="text-lg font-semibold text-on-surface font-headline mb-4">详细数据</h3>
        <p className="text-sm text-on-surface-variant font-body mb-3">点击结论行查看具体人员</p>
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-surface-container-low">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-on-surface-variant uppercase tracking-wider font-label">结论</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-on-surface-variant uppercase tracking-wider font-label">颜色</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-on-surface-variant uppercase tracking-wider font-label">人数</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-on-surface-variant uppercase tracking-wider font-label">占比</th>
            </tr>
          </thead>
          <tbody className="bg-surface-container-lowest divide-y divide-gray-200">
            {statistics.conclusions.map((detail) => (
              <tr
                key={detail.conclusionTitle}
                className="hover:bg-blue-50 cursor-pointer transition-colors"
                onClick={() => handleConclusionClick(detail)}
              >
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-600">
                  {detail.conclusionTitle}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span
                    className="inline-block w-4 h-4 rounded-full"
                    style={{ backgroundColor: getLevelColor(detail.level) }}
                  />
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-on-surface-variant font-body">{detail.count}人</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-on-surface-variant font-body">{detail.percentage.toFixed(1)}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Drill-down Modal */}
      {modalOpen && modalConclusion && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black bg-opacity-50" onClick={closeModal} />
          <div className="relative bg-surface-container-lowest rounded-3xl shadow-xl w-full max-w-4xl max-h-[85vh] overflow-hidden mx-4">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4" style={{ borderBottom: '1px solid rgba(171, 179, 183, 0.2)' }}>
              <div className="flex items-center gap-3">
                <h3 className="text-lg font-semibold text-on-surface font-headline">
                  {modalConclusion.conclusionTitle}
                </h3>
                <span
                  className="inline-block w-4 h-4 rounded-full"
                  style={{ backgroundColor: getLevelColor(modalConclusion.level) }}
                />
                <span className="text-sm text-on-surface-variant font-label">
                  共 {modalConclusion.count} 人
                </span>
              </div>
              <button
                onClick={closeModal}
                className="text-on-surface-variant hover:text-on-surface text-2xl leading-none"
              >
                &times;
              </button>
            </div>

            {/* Modal Body */}
            <div className="overflow-y-auto" style={{ maxHeight: 'calc(85vh - 73px)' }}>
              {loadingUsers ? (
                <div className="flex justify-center items-center py-16">
                  <div className="text-on-surface-variant font-body">加载中...</div>
                </div>
              ) : conclusionUsers.length === 0 ? (
                <div className="flex justify-center items-center py-16">
                  <div className="text-on-surface-variant font-body">暂无数据</div>
                </div>
              ) : (
                <table className="min-w-full">
                  <thead className="bg-surface-container-low sticky top-0">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-on-surface-variant uppercase font-label">姓名</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-on-surface-variant uppercase font-label">学号</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-on-surface-variant uppercase font-label">学校</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-on-surface-variant uppercase font-label">班级</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-on-surface-variant uppercase font-label">得分</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-on-surface-variant uppercase font-label w-10"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {conclusionUsers.map((user) => (
                      <React.Fragment key={user.answerId}>
                        <tr
                          className="hover:bg-blue-50 cursor-pointer transition-colors"
                          onClick={() => handleUserClick(user.answerId)}
                        >
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-on-surface">
                            {user.userName || '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-on-surface-variant">
                            {user.studentNumber || '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-on-surface-variant">
                            {user.schoolName || '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-on-surface-variant">
                            {user.className || '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-on-surface">
                            {user.score?.toFixed(2) ?? '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-on-surface-variant">
                            {expandedAnswerId === user.answerId ? '▲' : '▼'}
                          </td>
                        </tr>
                        {expandedAnswerId === user.answerId && (
                          <tr>
                            <td colSpan={6} className="px-6 py-0">
                              <div className="bg-surface-container-low rounded-2xl p-4 my-2 shadow-soft">
                                {loadingAnswer === user.answerId ? (
                                  <div className="text-center text-on-surface-variant py-4 font-body">加载回答详情...</div>
                                ) : answerDetails[user.answerId] ? (
                                  <div className="space-y-3">
                                    {/* Answer Details */}
                                    <h4 className="text-sm font-semibold text-on-surface font-label mb-2">回答详情</h4>
                                    {answerDetails[user.answerId].answerDetails
                                      .filter((d) => d.questionType !== 'DESCRIPTION')
                                      .map((d) => (
                                        <div key={d.questionId} className="text-sm">
                                          <AnswerDetailBlock detail={d} />
                                        </div>
                                      ))}

                                    {/* Indicator Results */}
                                    {answerDetails[user.answerId].indicatorResults.length > 0 && (
                                      <>
                                        <hr style={{ borderTop: '1px solid rgba(171, 179, 183, 0.2)' }} />
                                        <h4 className="text-sm font-semibold text-on-surface font-label mb-2">指标结果</h4>
                                        {answerDetails[user.answerId].indicatorResults.map((ir) => (
                                          <div key={ir.indicatorId} className="flex items-center gap-2 text-sm">
                                            <span className="text-on-surface font-medium font-body">{ir.indicatorName}:</span>
                                            <span className="text-on-surface font-semibold font-body">{ir.score?.toFixed(2) ?? '-'}</span>
                                            {ir.conclusionTitle && (
                                              <span
                                                className="px-2 py-0.5 text-xs font-medium rounded-full text-white"
                                                style={{ backgroundColor: getLevelColor(ir.level) }}
                                              >
                                                {ir.conclusionTitle}
                                              </span>
                                            )}
                                          </div>
                                        ))}
                                      </>
                                    )}
                                  </div>
                                ) : (
                                  <div className="text-center text-on-surface-variant py-4 font-body">加载失败</div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Class Statistics View
interface ClassStatisticsViewProps {
  stats: ClassStatistics[];
}

const ClassStatisticsView: React.FC<ClassStatisticsViewProps> = ({ stats }) => {
  const chartData = stats.map((s) => ({
    name: s.className,
    averageScore: Number(s.averageScore.toFixed(2)),
    studentCount: s.studentCount,
  }));

  return (
    <div className="bg-surface-container-lowest rounded-3xl shadow-soft p-6 space-y-4">
      <h3 className="text-lg font-semibold text-on-surface font-headline">班级对比</h3>
      <ResponsiveContainer width="100%" height={Math.max(200, stats.length * 40)}>
        <BarChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="name" tick={{ fontSize: 12 }} />
          <YAxis />
          <Tooltip
            formatter={(value: any, name: any) => {
              if (name === '平均分') return [Number(value).toFixed(2), name];
              return [value, name];
            }}
          />
          <Bar dataKey="averageScore" name="平均分" fill="#3b82f6" radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
      <table className="min-w-full divide-y divide-gray-200 text-sm">
        <thead className="bg-surface-container-low">
          <tr>
            <th className="px-4 py-2 text-left text-xs font-medium text-on-surface-variant font-label">班级</th>
            <th className="px-4 py-2 text-left text-xs font-medium text-on-surface-variant font-label">平均分</th>
            <th className="px-4 py-2 text-left text-xs font-medium text-on-surface-variant font-label">人数</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {stats.map((stat) => (
            <tr key={stat.classId} className="hover:bg-surface-container-low">
              <td className="px-4 py-2 font-medium text-on-surface font-body">{stat.className}</td>
              <td className="px-4 py-2 text-on-surface-variant font-body">{stat.averageScore.toFixed(2)}</td>
              <td className="px-4 py-2 text-on-surface-variant font-body">{stat.studentCount}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Grade Statistics View
interface GradeStatisticsViewProps {
  stats: GradeStatistics[];
}

const GradeStatisticsView: React.FC<GradeStatisticsViewProps> = ({ stats }) => {
  const chartData = stats.map((s) => ({
    name: s.grade,
    averageScore: Number(s.averageScore.toFixed(2)),
    studentCount: s.studentCount,
  }));

  return (
    <div className="bg-surface-container-lowest rounded-3xl shadow-soft p-6 space-y-4">
      <h3 className="text-lg font-semibold text-on-surface font-headline">年级对比</h3>
      <ResponsiveContainer width="100%" height={Math.max(200, stats.length * 40)}>
        <BarChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="name" tick={{ fontSize: 12 }} />
          <YAxis />
          <Tooltip
            formatter={(value: any, name: any) => {
              if (name === '平均分') return [Number(value).toFixed(2), name];
              return [value, name];
            }}
          />
          <Bar dataKey="averageScore" name="平均分" fill="#10b981" radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
      <table className="min-w-full divide-y divide-gray-200 text-sm">
        <thead className="bg-surface-container-low">
          <tr>
            <th className="px-4 py-2 text-left text-xs font-medium text-on-surface-variant font-label">年级</th>
            <th className="px-4 py-2 text-left text-xs font-medium text-on-surface-variant font-label">平均分</th>
            <th className="px-4 py-2 text-left text-xs font-medium text-on-surface-variant font-label">人数</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {stats.map((stat) => (
            <tr key={stat.grade} className="hover:bg-surface-container-low">
              <td className="px-4 py-2 font-medium text-on-surface font-body">{stat.grade}</td>
              <td className="px-4 py-2 text-on-surface-variant font-body">{stat.averageScore.toFixed(2)}</td>
              <td className="px-4 py-2 text-on-surface-variant font-body">{stat.studentCount}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ============================================================
// 答题结果 tab 子组件
// ============================================================
// 单条答题明细的"好看"渲染：
//   - MATRIX：左列题项 / 右列选中选项的两列表格
//   - 其它题型：题型 chip + 题目占一行，答案在下方左侧带边线缩进，
//     whitespace-pre-wrap 保留换行（多选 "A、B、C" / 填空多空 等）
// 解析全部容错：拿不到 schema 就 fallback 到 answerText 文本
// ============================================================

interface AnswerDetailBlockProps {
  detail: {
    questionType: string;
    questionTitle: string;
    answerText: string;
    questionSettings?: string | null;
    answerRaw?: string | null;
    score?: number | null;
  };
}

const QUESTION_TYPE_LABEL: Record<string, string> = {
  SINGLE_CHOICE: '单选',
  MULTIPLE_CHOICE: '多选',
  TEXT: '文本',
  SHORT_TEXT: '短文本',
  SLIDER: '滑块',
  MATRIX: '矩阵',
  DESCRIPTION: '说明',
  FILL_IN_BLANK: '填空',
  DYNAMIC_FILL_IN_BLANK: '动态填空',
  DATE: '日期',
  DROPDOWN: '下拉',
  AUDIO_RECORD: '音频',
  SELF_SCHEMA: '自我图式',
};

const AnswerDetailBlock: React.FC<AnswerDetailBlockProps> = ({ detail }) => {
  const typeLabel = QUESTION_TYPE_LABEL[detail.questionType] || detail.questionType;

  // MATRIX：schema-aware 表格渲染
  if (detail.questionType === 'MATRIX') {
    let rows: string[] = [];
    let answerRows: Record<string, string> = {};
    try {
      const settings = detail.questionSettings ? JSON.parse(detail.questionSettings) : null;
      if (settings && Array.isArray(settings.rows)) {
        rows = settings.rows
          .map((r: any) =>
            typeof r === 'string' ? r : (r?.title ?? r?.label ?? r?.name ?? '')
          )
          .filter((x: string) => x);
      }
    } catch { /* 损坏的 settings → fallback */ }
    try {
      const raw = detail.answerRaw ? JSON.parse(detail.answerRaw) : null;
      if (raw && typeof raw === 'object' && raw.rows && typeof raw.rows === 'object') {
        answerRows = raw.rows as Record<string, string>;
      }
    } catch { /* 损坏的 answerRaw → fallback */ }

    if (rows.length > 0) {
      return (
        <div className="space-y-2">
          <div className="flex items-start gap-2">
            <span className="inline-block px-1.5 py-0.5 bg-primary-100 text-primary-700 rounded text-xs shrink-0 font-medium font-label">
              {typeLabel}
            </span>
            <span className="text-on-surface font-medium font-body">{detail.questionTitle}</span>
          </div>
          <div className="ml-3 overflow-hidden rounded-xl border border-outline-variant">
            <table className="w-full text-sm">
              <thead className="bg-surface-container-low text-on-surface-variant">
                <tr>
                  <th className="px-3 py-2 text-left font-medium font-label w-[70%]">题项</th>
                  <th className="px-3 py-2 text-left font-medium font-label">选项</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, idx) => {
                  const v = answerRows[row];
                  return (
                    <tr key={idx} className={idx % 2 ? 'bg-surface-container-lowest' : 'bg-white'}>
                      <td className="px-3 py-2 text-on-surface font-body align-top">{row}</td>
                      <td className="px-3 py-2 font-body align-top">
                        {v != null && String(v).trim() !== ''
                          ? <span className="text-on-surface">{String(v)}</span>
                          : <span className="text-on-surface-variant">—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      );
    }
    // schema 拿不到就退化到通用文本布局
  }

  // 选择题（单选/多选/下拉）— 答案渲染成 tag pill 便于分辨
  const isChoice = detail.questionType === 'SINGLE_CHOICE'
    || detail.questionType === 'MULTIPLE_CHOICE'
    || detail.questionType === 'DROPDOWN';
  if (isChoice && detail.answerText) {
    // answerText 形态：单选直接是选项内容；多选是 "A、B、C"
    const tags = detail.answerText.split(/、/).map(s => s.trim()).filter(Boolean);
    return (
      <div className="space-y-1">
        <div className="flex items-start gap-2">
          <span className="inline-block px-1.5 py-0.5 bg-primary-100 text-primary-700 rounded text-xs shrink-0 font-medium font-label">
            {typeLabel}
          </span>
          <span className="text-on-surface font-medium font-body">{detail.questionTitle}</span>
        </div>
        <div className="pl-3 ml-3 border-l-2 border-surface-container-high">
          <div className="flex flex-wrap gap-1.5">
            {tags.length === 0 ? (
              <span className="text-on-surface-variant font-body">—</span>
            ) : tags.map((t, i) => (
              <span
                key={i}
                className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-primary-50 text-primary-700 border border-primary-100"
              >
                {t}
              </span>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // 其它题型 / MATRIX schema fallback
  return (
    <div className="space-y-1">
      <div className="flex items-start gap-2">
        <span className="inline-block px-1.5 py-0.5 bg-primary-100 text-primary-700 rounded text-xs shrink-0 font-medium font-label">
          {typeLabel}
        </span>
        <span className="text-on-surface font-medium font-body">{detail.questionTitle}</span>
      </div>
      <div className="pl-3 ml-3 border-l-2 border-surface-container-high">
        <span className="text-on-surface whitespace-pre-wrap font-body block">
          {detail.answerText || '—'}
        </span>
      </div>
    </div>
  );
};

// ============================================================

interface AnswerListViewProps {
  questionnaireId: number;
  /** 上层联动的学校名（match 答题快照中的 schoolName） */
  schoolName?: string;
  /** 上层联动的班级名（match 答题快照中的 className） */
  className?: string;
  /** 上层联动的学年（match classId 前缀；学生答题中没有直接字段，只能通过 className 过滤上层已限定） */
  academicYear?: string;
}

const PAGE_SIZE = 100;

/** 格式化答题时长（秒）→ "Xm Ys" / "Ys"；null/负值显示 "—"。 */
function formatDuration(seconds?: number | null): string {
  if (seconds == null || seconds < 0) return '—';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  if (m === 0) return `${s}秒`;
  return `${m}分${s > 0 ? `${s}秒` : ''}`;
}

const AnswerListView: React.FC<AnswerListViewProps> = ({
  questionnaireId, schoolName, className: scopedClassName, academicYear,
}) => {
  const [answers, setAnswers] = useState<AnswerListItem[]>([]);
  const [totalElements, setTotalElements] = useState(0);
  const [page, setPage] = useState(0);
  const [statusFilter, setStatusFilter] = useState<AnswerStatus | ''>('');
  const [durationFilter, setDurationFilter] = useState<DurationBucket | ''>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [actingId, setActingId] = useState<number | null>(null);

  // 作废 / 恢复一份答卷，成功后就地更新该行（valid / 作废元数据 / 指标结果）。
  const handleInvalidate = async (a: AnswerListItem) => {
    const reason = window.prompt('请输入作废原因（可留空）：', a.invalidReason || '');
    if (reason === null) return;   // 用户取消
    try {
      setActingId(a.id);
      const updated = await answerService.invalidateAnswer(a.id, reason || undefined);
      setAnswers((prev) => prev.map((x) => (x.id === a.id ? { ...x, ...updated } : x)));
    } catch (e: any) {
      window.alert(e?.response?.data?.message || '作废失败');
    } finally {
      setActingId(null);
    }
  };
  const handleRestore = async (a: AnswerListItem) => {
    if (!window.confirm('确认恢复该答卷为有效？将重新计入统计与指标。')) return;
    try {
      setActingId(a.id);
      const updated = await answerService.restoreAnswer(a.id);
      setAnswers((prev) => prev.map((x) => (x.id === a.id ? { ...x, ...updated } : x)));
    } catch (e: any) {
      window.alert(e?.response?.data?.message || '恢复失败');
    } finally {
      setActingId(null);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        // schoolName / scopedClassName 都走服务端 filter（按 answer.school_name_snapshot 过滤），
        // 保证 totalElements 和分页都正确（之前只 filter 当页导致空列表 bug）。
        const data = await answerService.getQuestionnaireAnswers(
          questionnaireId, page, PAGE_SIZE,
          statusFilter || undefined,
          schoolName || undefined,
          scopedClassName || undefined,
          durationFilter || undefined,
        );
        setAnswers(data?.content ?? []);
        setTotalElements(data?.totalElements ?? 0);
      } catch (e: any) {
        setError(e?.response?.data?.message || '加载失败');
      } finally {
        setLoading(false);
      }
    })();
  }, [questionnaireId, page, statusFilter, schoolName, scopedClassName, durationFilter]);

  // 切换问卷 / 学校 / 班级 / 状态 / 时长筛选时，回到第 1 页
  useEffect(() => {
    setPage(0);
  }, [questionnaireId, schoolName, scopedClassName, statusFilter, durationFilter]);

  // 学年过滤：答题列表没有直接的学年字段，但学校+班级层面已收口；这里不再额外过滤。
  // 姓名/学号搜索保留客户端只筛当页（少量场景使用，加服务端搜索过度设计）。
  const filtered = useMemo(() => {
    void academicYear;
    const k = search.trim().toLowerCase();
    if (!k) return answers;
    return answers.filter(a =>
      (a.userName || '').toLowerCase().includes(k)
      || (a.studentNumber || '').toLowerCase().includes(k)
    );
  }, [answers, search, academicYear]);

  const totalPages = Math.max(1, Math.ceil(totalElements / PAGE_SIZE));

  if (loading) {
    return <div className="bg-white rounded-3xl shadow-soft p-12 text-center text-on-surface-variant">加载中...</div>;
  }
  if (error) {
    return <div className="bg-danger-50 border border-danger-200 text-danger-800 px-4 py-3 rounded-xl">{error}</div>;
  }

  return (
    <div className="bg-white rounded-3xl shadow-soft p-6">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h2 className="text-lg font-bold font-headline text-on-surface">
          答题结果
          <span className="ml-2 text-sm font-normal text-on-surface-variant">
            共 {totalElements} 份
            {statusFilter && `（${statusFilter === 'COMPLETED' ? '已完成' : statusFilter === 'IN_PROGRESS' ? '进行中' : '未开始'}）`}
            {totalPages > 1 && `，第 ${page + 1} / ${totalPages} 页（每页 ${PAGE_SIZE}）`}
            {filtered.length !== answers.length && `，当页筛选后 ${filtered.length} 份`}
          </span>
        </h2>
        <div className="flex items-center gap-2">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter((e.target.value || '') as AnswerStatus | '')}
            className="h-10 px-3 bg-white border border-outline-variant rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary"
            title="按问卷状态过滤"
          >
            <option value="">全部状态</option>
            <option value="COMPLETED">已完成</option>
            <option value="IN_PROGRESS">进行中</option>
            <option value="PENDING">未开始</option>
          </select>
          <select
            value={durationFilter}
            onChange={(e) => setDurationFilter((e.target.value || '') as DurationBucket | '')}
            className="h-10 px-3 bg-white border border-outline-variant rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary"
            title="按答题时长过滤"
          >
            <option value="">全部时长</option>
            {DURATION_BUCKETS.map((b) => (
              <option key={b.value} value={b.value}>{b.label}</option>
            ))}
          </select>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="搜索姓名 / 学号（仅当页）"
            className="h-10 px-3 w-64 bg-white border border-outline-variant rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary"
          />
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-12 text-on-surface-variant">没有匹配的答题</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left bg-surface-container-low text-on-surface-variant">
                <th className="px-3 py-2 font-medium">学生</th>
                <th className="px-3 py-2 font-medium">学号</th>
                <th className="px-3 py-2 font-medium">学校 / 班级</th>
                <th className="px-3 py-2 font-medium">最后修改</th>
                <th className="px-3 py-2 font-medium">回答时长</th>
                <th className="px-3 py-2 font-medium">状态</th>
                <th className="px-3 py-2 font-medium text-right">操作</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((a) => (
                <React.Fragment key={a.id}>
                  <tr className={`border-t border-outline-variant hover:bg-surface-container-low ${a.valid === false ? 'opacity-60' : ''}`}>
                    <td className="px-3 py-2 font-medium">{a.userName || '—'}</td>
                    <td className="px-3 py-2 text-on-surface-variant">{a.studentNumber || '—'}</td>
                    <td className="px-3 py-2 text-on-surface-variant">
                      {a.schoolName ? `${a.schoolName} · ${a.className ?? ''}` : '—'}
                    </td>
                    <td className="px-3 py-2 text-on-surface-variant">
                      {a.submittedAt ? new Date(a.submittedAt).toLocaleString() : '—'}
                    </td>
                    <td className="px-3 py-2 text-on-surface-variant whitespace-nowrap">
                      {formatDuration(a.durationSeconds)}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex flex-wrap items-center gap-1">
                        {a.completionStatus === 'COMPLETED' ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 border border-green-200">已完成</span>
                        ) : a.completionStatus === 'IN_PROGRESS' ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700 border border-orange-200">进行中</span>
                        ) : (
                          <span className="text-xs text-on-surface-variant">—</span>
                        )}
                        {a.valid === false && (
                          <span
                            className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700 border border-red-200"
                            title={a.invalidReason ? `作废原因：${a.invalidReason}` : '已作废'}
                          >
                            已作废
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <button
                        onClick={() => setExpandedId(expandedId === a.id ? null : a.id)}
                        className="text-sm text-primary hover:underline"
                      >
                        {expandedId === a.id ? '收起' : '查看答案'}
                      </button>
                      {a.valid === false ? (
                        <button
                          onClick={() => handleRestore(a)}
                          disabled={actingId === a.id}
                          className="ml-3 text-sm text-green-600 hover:underline disabled:opacity-50"
                        >
                          {actingId === a.id ? '处理中…' : '恢复'}
                        </button>
                      ) : (
                        <button
                          onClick={() => handleInvalidate(a)}
                          disabled={actingId === a.id}
                          className="ml-3 text-sm text-red-600 hover:underline disabled:opacity-50"
                        >
                          {actingId === a.id ? '处理中…' : '作废'}
                        </button>
                      )}
                    </td>
                  </tr>
                  {expandedId === a.id && (
                    <tr className="bg-primary-50/30">
                      <td colSpan={7} className="px-3 py-3">
                        <div className="space-y-4">
                          {a.valid === false && (
                            <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2 rounded-xl">
                              此答卷已作废，不计入统计 / 指标 / 导出。
                              {a.invalidReason && <span>　作废原因：{a.invalidReason}</span>}
                              {a.invalidatedAt && <span>　作废时间：{new Date(a.invalidatedAt).toLocaleString()}</span>}
                            </div>
                          )}
                          {/* 该学生的指标结果（原在列表每行，现移到查看答案详情里） */}
                          <div>
                            <div className="text-xs font-medium text-on-surface-variant mb-1.5">指标结果</div>
                            <div className="flex flex-wrap gap-1">
                              {(a.indicatorResults || []).map((r, idx) => (
                                <span
                                  key={idx}
                                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
                                  style={{
                                    backgroundColor: (r.level || '#94a3b8') + '20',
                                    color: r.level || '#475569',
                                    border: `1px solid ${r.level || '#94a3b8'}40`,
                                  }}
                                >
                                  {r.indicatorName} {r.score?.toFixed?.(1) ?? r.score}
                                  {r.conclusionTitle && ` · ${r.conclusionTitle}`}
                                </span>
                              ))}
                              {(!a.indicatorResults || a.indicatorResults.length === 0) && (
                                <span className="text-xs text-on-surface-variant">该答卷暂无指标结果</span>
                              )}
                            </div>
                          </div>
                          {(a.answerDetails || [])
                            .filter((d) => d.questionType !== 'DESCRIPTION')
                            .map((d, idx) => (
                              <div key={idx}>
                                <AnswerDetailBlock detail={d} />
                                {d.score != null && (
                                  <div className="ml-3 mt-1 text-xs text-on-surface-variant">得分：{d.score}</div>
                                )}
                              </div>
                            ))}
                          {(!a.answerDetails || a.answerDetails.length === 0) && (
                            <div className="text-sm text-on-surface-variant">无答题明细</div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-outline-variant text-sm">
          <div className="text-on-surface-variant">
            显示 {page * PAGE_SIZE + 1} – {Math.min((page + 1) * PAGE_SIZE, totalElements)} / 共 {totalElements} 份
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage(0)}
              disabled={page === 0}
              className="px-3 py-1 rounded-lg border border-outline-variant disabled:opacity-40 disabled:cursor-not-allowed hover:bg-surface-container-low"
            >首页</button>
            <button
              onClick={() => setPage(p => Math.max(0, p - 1))}
              disabled={page === 0}
              className="px-3 py-1 rounded-lg border border-outline-variant disabled:opacity-40 disabled:cursor-not-allowed hover:bg-surface-container-low"
            >上一页</button>
            <span className="px-2">{page + 1} / {totalPages}</span>
            <button
              onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
              className="px-3 py-1 rounded-lg border border-outline-variant disabled:opacity-40 disabled:cursor-not-allowed hover:bg-surface-container-low"
            >下一页</button>
            <button
              onClick={() => setPage(totalPages - 1)}
              disabled={page >= totalPages - 1}
              className="px-3 py-1 rounded-lg border border-outline-variant disabled:opacity-40 disabled:cursor-not-allowed hover:bg-surface-container-low"
            >末页</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default StatisticsPage;
